import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, Injectable, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, catchError, combineLatest, filter, forkJoin, of, switchMap, take, tap, finalize } from "rxjs";
import { AccessLevel, Application, DataContainer, FormPageType, iApplyFormControl, NavigationStyle, ProductDefinition, RuleType, TruthCondition } from "app/shared/model";
import { RuleEvaluatorService } from 'app/shared/services/rules/rule-evaluator.service';
import { ApplicationsServiceProxy } from 'app/shared/services/application-service.proxy';
import { FirstValidationPage, ISubmitApplicationInput, LastVisitedPage, SaveApplicationResponse, SubmitApplicationResponse } from 'app/shared/services/model/application-service.model';
import { FormService } from "../../form.service";
import { ValidationResult, ValidationService } from '../../application-editor/validation';
import { hasValue, isDefined, isIterable, toJson } from 'app/shared';
import { FormAccessEntitlements } from 'app/shared/services/model/form-service.model';
import { GlobalService } from 'app/shared/services/global.service';
import { AlertService } from '@full-fledged/alerts';
import { CommunicatorService } from 'app/shared/services/communicator.service';
import { VisibilityChangeModel } from 'app/shared/services/model/communicatorModel';
import { FormNavigationItem } from './navigation.model';
import { Title } from '@angular/platform-browser';
import { DataSharingService } from 'app/shared/services/data-sharing.service';

@Injectable()
export class FormNavigationService
{
    private readonly destroyRef = inject(DestroyRef);
    private readonly title: Title = inject(Title);
    private readonly _alertService = inject(AlertService);
    private readonly _applicationsServiceProxy = inject(ApplicationsServiceProxy);
    private readonly _formService = inject(FormService);
    private readonly _globalService = inject(GlobalService);
    private readonly _router = inject(Router);
    private readonly _ruleEvaluatorService = inject(RuleEvaluatorService);
    private readonly _validationService = inject(ValidationService);
    private readonly _communicatorService = inject(CommunicatorService);
    private readonly _dataSharingService = inject(DataSharingService);

    // local values...
    private _menuItems: FormNavigationItem[];

    private readonly knownRoutes = [
        { from: '/#/vault', to: '/form-vault' },
        { from: '/#/dashboard', to: '/dashboard' },
        { from: '/#/user/profile', to: '/account/profile' },
    ];

    product$: Observable<ProductDefinition>;
    menuItems$: Observable<FormNavigationItem[]>;
    validationResult$: Observable<ValidationResult>;
    application$: Observable<Application>;
    applicationData$: Observable<DataContainer | null>;

    currentMenuItem = signal<FormNavigationItem>(null);
    currentPageMenuItem = signal<FormNavigationItem>(null);

    url$: BehaviorSubject<FormNavigationItem> = new BehaviorSubject(null);
    _currentMenuItem: BehaviorSubject<FormNavigationItem> = new BehaviorSubject(null);
    _nextMenuItem: BehaviorSubject<FormNavigationItem> = new BehaviorSubject(null);
    _previousMenuItem: BehaviorSubject<FormNavigationItem> = new BehaviorSubject(null);

    product: ProductDefinition;
    application: Application;
    applicationEntitlement: FormAccessEntitlements;

    allowForwardNavigation: boolean;
    surveyResult: any;
    applicationData: any;
    applicationStateValue: string;
    canAutoSave: boolean = false;
    preview: boolean = false;

    progress = signal<number>(0);

    closeNavigationUrl = signal('') // : string = '';
    closeText = signal('Close'); //: string = 'Close';

    // A signal indicating if the current form navigation mode is "Full Screen".
    isFullScreen = signal<boolean>(false);

    isBusy = signal<boolean>(false);

    // Can go back if not on first accessible page.
    canNavigateBackward = signal<boolean>(false);

    // Can go forward if on a user page, and that page does not submit.
    canNavigateForward = signal<boolean>(false);

    // Can close if the close button is enabled for the given form.
    canClose = signal<boolean>(false);

    // Can save if: application.AccessLevel === AccessLevel.Write && metadata.AllowSaving && !repeaterPageIsOpen
    //  - Hidden if: "preview || submits || pageType === 'receipt' || applicationEntitlement.BaseAccess !== 'W' || (!applicationEntitlement.ManualSaveAllowed && isOnline)"
    //  - Disabled if: "$root.navigationDisabled || busyButtons.save"
    canSave = signal<boolean>(false);

    // Can submit if on a submit page and the user has write access, and the workflow state allows submit.
    canSubmit = signal<boolean>(false);

    // updates to reflect the Form Title + currently selected Menu Title.
    formTitle = signal<string>('');
    menuTitle = signal<string>('');

    get currentMenuItem$(): Observable<FormNavigationItem> {
        return this._currentMenuItem.asObservable();
    }

    get nextMenuItem$() {
        return this._nextMenuItem.asObservable();
    }

    get previousMenuItem$() {
        return this._previousMenuItem.asObservable();
    }

    constructor() {
        this.product$ = this._formService.product$;
        this.menuItems$ = this._formService.menuItems$;
        this.applicationData$ = this._formService.applicationData$;
        this.application$ = this._formService.application$;

        this.validationResult$ = this._validationService.validationResult$;

        this._formService.product$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (product: ProductDefinition) => {
                    this.product = product;
                    this.allowForwardNavigation = product?.allowForwardNavigation || false;

                    this.closeNavigationUrl.set(this.normalizeCloseNavigationUrl(product?.closeNavigationAddress));
                    this.closeText.set(product?.closeButtonText?.length > 0 ? product.closeButtonText : 'Close');
                    this.isFullScreen.set(product?.navigationStyle == NavigationStyle.FullScreen);
                    this.formTitle.set(product?.name);
                },
                error: console.error
            });

        this._formService.applicationData$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: DataContainer) => this.applicationData = data,
                error: console.error
            });

        this._formService.application$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (app: Application) => this.application = app,
                error: console.error
            });

        this._formService.entitlements$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entitlements: FormAccessEntitlements) => {
                    this.applicationEntitlement = entitlements;
                    this.canAutoSave = entitlements?.autoSaveEnabled;
                    // console.log('form entitlements: ', this.applicationEntitlement, this.canAutoSave);
                },
                error: console.error
            });

        this._formService.menuItems$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (menuItems: FormNavigationItem[]) => {
                    this._menuItems = menuItems;

                    // When the menu has loaded, create Visibility Watch
                    this.watchVisibility();
                    this.doVisibilityRun(this._menuItems);

                    let currentMenuItem = this._currentMenuItem.value;
                    if (!currentMenuItem) {
                        let item = this.traverse(this._menuItems);
                        this.setCurrentMenuItem(item);
                        return;
                    }

                    // let newMenuItem = this.findMenuItemById(currentMenuItem.pageId, currentMenuItem.controlId);
                    this._resetNavigationState(currentMenuItem);
                },
                error: console.error
            });

        this.currentMenuItem$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (item: FormNavigationItem) => this._resetNavigationState(item),
                error: console.error
            });

        this._formService.lastVisitedPage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (item: LastVisitedPage) => {
                    // only trigger in resolveApplicationDependencies
                    // without pageId in URL and with autoOpenLastVisitedPage
                    let state = this._formService.currentState;
                    if (item?.pageId) {
                        // Check if the pageId specified has read/write permission...
                        const menuItem = this.findMenuItem(this._menuItems, item.pageId);
                        if (menuItem?.accessLevel > AccessLevel.NoAccess) {
                            this._router.navigate(['form', state.formId, 'app', state.applicationId, { page: item.pageId }]);
                        }
                    }
                },
                error: console.error
            });

        // Update the Page Title
        effect(() => {
            if (this.formTitle() && this.menuTitle()) {
                this.title.setTitle(this.formTitle() + ' - ' + this.menuTitle());
            }
        });
    }

    _resetNavigationState(currentMenuItem: FormNavigationItem)
    {
        // TODO: Handle "SUBMIT" pages by workflow state...

        if (!this._menuItems?.length) {
            return;
        }

        if (!currentMenuItem) {
            this.checkValidState();
            return;
        }

        let menuItem = this.findFirstAccessibleSubMenu(currentMenuItem);
        if (menuItem != currentMenuItem) {
            this.navigate(menuItem);
            return;
        }

        if (!this.accessible(currentMenuItem)) {
            // If the "currentMenuItem" is not accessible, get the next sibling.
            let menuItem = this.traverse(this._menuItems, currentMenuItem.sortIndex, false);
            if (menuItem) {
                this.navigate(menuItem);
                return;
            }

            // If there are no accessible siblings after the current menu item, start form the beginning.
            return;
        }

        this.menuTitle.set(menuItem.title);

        this.updateCanSubmit(menuItem);
        this.updateCanSave(menuItem);

        this.canClose.set(this.product?.showCloseButton && currentMenuItem.pageType == FormPageType.Receipt);

        this._validationService.clearErrors();

        let previousMenuItem = this.getPreviousMenuItem(menuItem);
        this._previousMenuItem.next(previousMenuItem);
        this.canNavigateBackward.set(isDefined(previousMenuItem));

        let nextMenuItem = this.getNextMenuItem(menuItem);
        this._nextMenuItem.next(nextMenuItem);
        this.canNavigateForward.set(isDefined(nextMenuItem) && !this.canSubmit());

        this._formService.currentState.pageId = menuItem.pageId;
        this._formService.currentState.subPageId = menuItem.controlId;

        const currentPageIndex = currentMenuItem.pageTabIndex;
        this._dataSharingService.currentPageIndex = currentPageIndex < 0 ? 0 : currentPageIndex;

        this._formService.getExternalData(this.product, this.application, currentMenuItem.pageId).subscribe();
    }

    setCurrentMenuItem(item: FormNavigationItem) {
        if (item) {
            this._currentMenuItem.next(item);
            this.currentMenuItem.set(item);
            if (!item.controlId) {
                this.currentPageMenuItem.set(item);
            } else {
                let parent = this.findMenuItemById(item.pageId);
                this.currentPageMenuItem.set(parent);
            }
        }
    }

    setProgress(position: number, of: number) {
        of = of <= 0 ? 1 : of;
        position = position < 0 ? 0 : position;
        position = position > of ? of : position;

        let percentage = (position / of) * 100;
        let progress = Math.round(percentage);
        this.progress.set(progress);
    }

    updateCanSubmit(menuItem: FormNavigationItem) {
        let canSubmit = this.applicationEntitlement?.baseAccess === AccessLevel.Write
            && !this._formService.currentState.readonly
            && menuItem.pageType == FormPageType.User
            && menuItem.submitsApplication
            && this.isLastAccessibleSubMenuItem(menuItem);

        // TODO: Implement Submit Workflow State management

        this.canSubmit.set(canSubmit);
    }

    updateCanSave(menuItem: FormNavigationItem) {
        let canSave = this.applicationEntitlement?.baseAccess === AccessLevel.Write
            && this.applicationEntitlement.manualSaveAllowed
            && this.product.allowSaving
            && menuItem.pageType == FormPageType.User
            && !this.canSubmit()
            && !this.preview
            && !this._formService.currentState.readonly;

        this.canSave.set(canSave);
    }

    // MUST use lambda function format to ensure rebinding of this function into other services
    // or components does not modify the reference to "this".
    canNavigate = (item: FormNavigationItem): boolean =>
    {
        if (!item.visible()) {
            return false;
        }

        let isUserPage = item.pageType == FormPageType.User;
        if (this.allowForwardNavigation && isUserPage) {
            return true;
        }

        const itemIndex = this._menuItems?.findIndex(i => i.pageId === item.pageId);
        return (itemIndex <= (this._currentMenuItem?.getValue()?.pageTabIndex || 0));
    }

    navigate(menuItem?: FormNavigationItem) {
        menuItem = this.findFirstAccessibleSubMenu(menuItem);
        // Check if we're already on that page - if so don't fo anything.
        if (this.ignoreMenuChange(menuItem)) {
            return;
        }

        // Clear any existing error messages before navigation
        this._validationService.clearErrors();

        let state = this._formService.currentState;

        if (menuItem.controlId) {
            this._router.navigate(['form', state.formId, 'app', state.applicationId, { page: menuItem.pageId, subPage: menuItem.controlId }]);
        } else {
            this._router.navigate(['form', state.formId, 'app', state.applicationId, { page: menuItem.pageId }]);
        }
    }

    navigateBackward(): void {
        if (this.isBusy()) {
            return;
        }
        // Clear any existing error messages before navigation
        this._validationService.clearErrors();
        this.navigate(this._previousMenuItem.getValue());
    }

    navigateForward(): void {
        // validate nav request...
        let nextMenuItem = this._nextMenuItem.getValue();
        // console.log('in navigate forward: -', nextMenuItem);
        if (!nextMenuItem) {
            return;
        }

        if (this.isBusy()) {
            return;
        }

        // Clear any existing error messages before validation
        this._validationService.clearErrors();

        if (this.applicationEntitlement.baseAccess !== AccessLevel.Write) {
            // if we're NOT allowed to write, validation and saving are DISABLED!
            // So, just navigate.
            this.navigate(this._nextMenuItem.getValue());
            return;
        }

        this.isBusy.set(true);

        // forkJoin: ConnectivityCheck and Local Validation can run asynchronously for performance.
        forkJoin({
            // 1. Verify Connection to the server.
            isOnline: this.checkOnline(),
            // 2. Validate the page locally.
            validation: this.validateApplicationLocal(true)
        })
            .pipe(
                take(1),
                finalize(() => this.isBusy.set(false))
            )
            .subscribe({
                next: (result) => {
                    if (!result.validation.success || !result.validation.valid) {
                        console.error('Validation Error:', result.validation);
                        return;
                    }

                    // TODO: 3. Validate the page server-side (if online).

                    // 4. Save if auto-save is turned on.
                    this.autoSave().subscribe(result => {
                        // 5. Navigate to the next page.
                        this.navigate(this._nextMenuItem.getValue());
                    });
                },
                error: (err) => console.error('NAVIGATION ERROR!!!', err)
            });
    }

    navigateToPage(pageId: number, subPageId?: number)// : Observable<FormNavigationItem>
    {
        // Clear any existing error messages before navigation
        this._validationService.clearErrors();

        this._formService.menuItems$
            .pipe(
                filter(x => !!x),
                take(1)
            )
            .subscribe((menuItems: FormNavigationItem[]) => {
                // console.log('Menu Items:', menuItems);
                let newMenuItem = (pageId > 0)
                    ? this.findMenuItem(menuItems, pageId, subPageId) // matching page & sub-page id.
                    : this.traverse(menuItems); // first page in form with entitlement to see.

                if (newMenuItem?.subItems.length && !subPageId) {
                    newMenuItem = this.findFirstAccessibleSubMenu(newMenuItem);
                }

                let state = this._formService.currentState;
                const currentMenuItem = this._currentMenuItem.value;
                if (newMenuItem?.pageId == state.pageId
                    && newMenuItem?.controlId == state.subPageId
                    && currentMenuItem?.pageId == state.pageId
                    && currentMenuItem?.controlId == state.subPageId
                ) {
                    return;
                }


                if (newMenuItem?.controlId != subPageId) {
                    let state = this._formService.currentState;
                    this._router.navigate([
                        'form',
                        state.formId,
                        'app',
                        state.applicationId,
                        { page: newMenuItem.pageId, subPage: newMenuItem.controlId }
                    ]);

                    return;
                }

                this.setCurrentMenuItem(newMenuItem);
            });
    }

    validateApplicationLocal(currentPageOnly: boolean = false): Observable<ValidationResult> {
        return combineLatest({
            product: this._formService.product$,
            applicationData: this._formService.applicationData$,
            menu: this.currentMenuItem$
        }).pipe(
            take(1),
            switchMap(data => {
                let container = data.applicationData;
                const menuItem = data.menu;
                const pageId = menuItem.pageId;
                const subPageId = menuItem.controlId;
                let allControls: iApplyFormControl[] = data.product.pages.map(page => page.controls).flat();
                // if (currentPageOnly) {
                //     if (subPageId) {
                //         let currentSubPage = data.product.pages.find(page => page.pageId == pageId).controls.find(control => control.id == subPageId);
                //         if (isGroupControl(currentSubPage)) {
                //             allControls = currentSubPage.controls.flat();
                //         }
                //     } else {
                //         allControls = data.product.pages.filter(page => page.pageId == pageId).map(page => page.controls).flat();
                //     }
                // }

                return this._validationService.validateApplication(allControls, container, pageId, subPageId);
            })
        );
    }

    validateApplicationServer(): Observable<ValidationResult> {
        return combineLatest({
            product: this._formService.product$,
            applicationData: this._formService.applicationData$
        }).pipe(
            take(1),
            switchMap(data => {
                let applicationData = data.applicationData;
                const pageId = this._currentMenuItem.getValue().pageId;
                const subPageId = this._currentMenuItem.getValue().controlId;
                return this._validationService.validateApplicationServerSide(applicationData, pageId, subPageId);
            })
        );
    }

    autoSave(): Observable<any> {
        return this.canAutoSave
            ? this.saveApplication(true)
            : of(true)
    }

    saveApplication(isAutoSave?: boolean, didCreateUser?: boolean, publicDidAuthenticate?: boolean): Observable<SaveApplicationResponse> //<{ success: boolean, message: string }>
    {
        if (!isAutoSave && this.isBusy()) {
            return of(null);
        }

        if (!isAutoSave) {
            this.isBusy.set(true);
        }

        const menu = this._currentMenuItem.getValue();
        const state = this._formService.currentState;

        // save applicationdata (ie. container data) into application --> data object
        return this._formService.applicationData$
            .pipe(
                take(1),
                switchMap((applicationData: DataContainer) => {
                    return this._formService.saveApplication({
                        formId: state.formId,
                        formVersion: state.formVersion,
                        applicationId: state.applicationId,
                        offlineApplicationId: state.offlineApplicationId,
                        applicationState: state.workflowState,
                        data: toJson(applicationData),
                        publicDidAuthenticate: publicDidAuthenticate ?? false,
                        publicDidCreateAccount: didCreateUser ?? false,
                        isAutoSave: isAutoSave ?? false,
                        pageId: menu.pageId,
                        subpageId: menu.controlId || null,
                        created: state.created,
                        applicationIdDisplay: state.applicationIdDisplay,
                        userId: this._globalService.getCurrentUserId()
                    });
                }),
                finalize(() => {
                    if (!isAutoSave) {
                        this.isBusy.set(false);
                    }
                })
            );
    }

    submitApplication(): Observable<SubmitApplicationResponse> {
        if (this.isBusy()) {
            return of(null);
        }

        this.isBusy.set(true);
        this._validationService.clearErrors();
        return forkJoin({
            // 1. Verify Connection to the server.
            isOnline: this.checkOnline(),
            // 2. Validate the page locally.
            validation: this.validateApplicationLocal(false)
        }).pipe(
            take(1),
            switchMap((result) => {
                let response = new SubmitApplicationResponse();

                if (!result.validation.success || !result.validation.valid) {
                    console.error(result.validation);
                    response.success = true;
                    return of(response);
                }
                // Remember where we are in the current menu structure...
                // let currentPageId = this._currentMenuItem.value.pageId;

                // 3. Submit!
                const submitPageId = this._formService.currentState.pageId;
                return this.callSubmitApplication()
                    .pipe(switchMap((submitAppResponse: SubmitApplicationResponse) => {
                        if (submitAppResponse.success) {
                            // TODO: 4. Re-sync nav-menu based on new workflow state and entitlements.
                            return this._formService.onApplicationSubmitted(submitAppResponse)
                                .pipe(
                                    tap(() => this.onSubmit(submitAppResponse, submitPageId)),
                                    switchMap(() => of(submitAppResponse))
                                );
                        }

                        return of(submitAppResponse);
                    }));
            }),
            finalize(() => this.isBusy.set(false))
        );
    }

    findMenuItemById(pageId: number, controlId?: number): FormNavigationItem
    {
        return this.findMenuItem(this._menuItems, pageId, controlId);
    }

    findMenuItem(items: FormNavigationItem[], pageId: number, controlId?: number): FormNavigationItem
    {
        let item = items.find(x =>
            (!pageId || x.pageId == pageId) &&
            (x.controlId == controlId ||
                (!x.controlId && !controlId)));

        if (item) {
            if (!item.subItems?.length) {
                // if the current menu item doesn't have children, it's a match.
                return item;
            }

            if (controlId) {
                // if we're searching for a specific child, find it in the children of this item.
                let child = this.findMenuItem(item.subItems, pageId, controlId);  // traverse(item.subItems);
                if (child) {
                    return child;
                }
            }

            return item;
        }

        // No menu has been found so far, so let's find it from the start.
        for (item of items) {
            if (item.subItems?.length) {
                let child = this.findMenuItem(item.subItems, pageId, controlId);
                if (child) {
                    return child;
                }
            }
        }

        return null;
    }

    closeApplication()
    {
        const closeUrl = this.normalizeCloseNavigationUrl(this.closeNavigationUrl());
        const fullUrlRegex = /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/;

        if (fullUrlRegex.test(closeUrl)){
            this._globalService.showModal("", "loading");
            this.navigateToExternalUrl(closeUrl);
        } else {
            this._router.navigateByUrl(closeUrl);
        }
    }

    private normalizeCloseNavigationUrl(closeNavigationAddress: string = ''): string
    {
        const closeUrl = closeNavigationAddress ?? '';
        const mappedRoute = this.knownRoutes.find(r => r.from.toLowerCase() == closeUrl.toLowerCase());
        if (mappedRoute) {
            return mappedRoute.to;
        }

        return closeUrl.replace(/^\/?#\//, '/');
    }

    private navigateToExternalUrl(url: string)
    {
        window.location.href = url;
    }

    /**
     * Executes an initial visibility state update for the menu items based on the current application data state.
     *
     * @param menuItems The Forms menu items.
     */
    private doVisibilityRun(menuItems: FormNavigationItem[]) {
        if (!isIterable(menuItems)) {
            return;
        }

        for (let item of menuItems) {
            if (item.accessLevel == AccessLevel.NoAccess) {
                // Set the visibility of the menu item to false if access level is "No Access".
                item.visible.set(false);
                continue;
            }

            if (item.controlId) {
                let parent = this.findMenuItem(this._menuItems, item.pageId, null);
                if (parent && !parent.visible()) {
                    item.visible.set(false);
                    continue;
                }
            }

            if (item.visibilityRule) {
                // Check current state of the controls visibility.
                let visible = this.getNavItemVisibilityStatus(item.visibilityRule, this.applicationData, this.surveyResult);
                item.visible.set(visible);
            }

            if (item.subItems?.length) {
                this.doVisibilityRun(item.subItems);
                this.checkTopLevelMenuVisibility(item);
            }
        }
    }

    private checkTopLevelMenuVisibility(menuItem: FormNavigationItem) {
        if (!menuItem || !menuItem.subItems?.length || menuItem.accessLevel == AccessLevel.NoAccess) {
            return;
        }

        // Check the visibility of all children of this menu item.
        // if ALL children are hidden, parent is hidden.
        // if ANY child is visible, parent is visible.
        let parentVisible = menuItem.subItems.some(item => item.visible()) || false;
        if (menuItem.visible() != parentVisible) {
            // Set the parent menu item visibility state.
            menuItem.visible.set(parentVisible);
        }
    }

    /**
     * Watches the visibility service change notifications for any references to
     * control groups that represent sub-page menu items, and maintains the visibility state.
     */
    private watchVisibility() {
        this._communicatorService.visibilityChanged$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (model: VisibilityChangeModel) => {
                    let controlId = <number>model.visibilityId;
                    let menuItem = this.findMenuItem(this._menuItems, null, controlId);
                    if (menuItem && menuItem.visible() != model.flag) {
                        // Check the Parent Menu Item for the state of children.
                        let parent = this.findMenuItem(this._menuItems, menuItem.pageId, null);
                        if (parent.accessLevel > AccessLevel.NoAccess) {
                            // Set current visibility state of the menu item.
                            menuItem.visible.set(model.flag);
                            this.checkTopLevelMenuVisibility(parent);
                        }
                        this._resetNavigationState(this._currentMenuItem.value);
                    }
                }
            });
    }

    private callSubmitApplication(appBody?: ISubmitApplicationInput): Observable<SubmitApplicationResponse> {
        const submitApplicationBody = appBody || this.createISubmitApplicationInput();
        return this._applicationsServiceProxy.submitApplication(submitApplicationBody);
    }

    private createISubmitApplicationInput(): ISubmitApplicationInput {
        let state = this._formService.currentState;
        let requestedState: any;
        this.application$.subscribe((application => {
            if (application) {
                requestedState = application.applicationState;
            }
        }));

        let metaData = {
            requestedState: requestedState,
            assignee: null,
            assigneeType: null
        };

        return <ISubmitApplicationInput>{
            formId: state.formId,
            applicationIdDisplay: state.applicationIdDisplay,
            formVersion: state.formVersion,
            applicationId: state.applicationId,
            offlineApplicationId: state.offlineApplicationId,
            data: toJson(this.applicationData),
            metadata: toJson(metaData),
            submitPageId: this._currentMenuItem.getValue().pageId,
            eventType: 'submit',
            isDirty: true,
        };
    }

    private ignoreMenuChange(menuItem: FormNavigationItem): boolean
    {
        let current = this._currentMenuItem.getValue();
        return !menuItem ||
            (current?.pageId == menuItem.pageId && current?.controlId == menuItem.controlId);
    }

    private onSubmit(result: SubmitApplicationResponse, submitPageId: number) {
        let state = this._formService.currentState;
        const firstValidationPage = this.normalizeFirstValidationPage(result.firstPage);
        const hasSubmitErrors = hasValue(result.errors);
        const isValidationFailure = result.valid === false && (hasSubmitErrors || firstValidationPage.pageId != null);

        // If there's a validation failure during submit, go back the the first control on the first page with an error.
        if (isValidationFailure) {
            this._validationService.queueServerValidationRedirect(result, firstValidationPage.pageId, firstValidationPage.subPageId);

            if (firstValidationPage.pageId == null) {
                const currentMenuItem = this._currentMenuItem.getValue();
                if (currentMenuItem?.pageId != null) {
                    this._validationService.applyPendingServerValidationForPage(currentMenuItem.pageId, currentMenuItem.controlId);
                }
                return;
            }

            const targetPageId = firstValidationPage.pageId;
            const targetSubPageId = firstValidationPage.subPageId;
            const params: { page: number, subPage?: number } = { page: targetPageId };
            if (targetSubPageId != null) {
                params.subPage = targetSubPageId;
            }

            this._router.navigate(['form', state.formId, 'app', state.applicationId, params])
                .then(() => {
                    if (hasSubmitErrors) {
                        this._validationService.applyPendingServerValidationForPage(targetPageId, targetSubPageId);
                    } else {
                        this.validateTargetPageServerSide(targetPageId, targetSubPageId);
                    }
                });
            return;
        }

        // Ensure the page has not already changed as the result of new entitlements after workflow transition.
        let currentMenuItem = this._currentMenuItem.getValue();
        let submitMenuItem = this.findMenuItemById(submitPageId);
        if (currentMenuItem?.pageId !== submitPageId && currentMenuItem.pageTabIndex > submitMenuItem.pageTabIndex) {
            return false;
        }

        // validate nav request...
        let nextMenuItem = this._nextMenuItem.getValue();
        if (!nextMenuItem) {
            return false;
        }

        if (this._globalService.isOnline()) {
            // When there's no errors, just navigate to the next page...
            if (result.redirectionUrl){
                document.location.href = result.redirectionUrl;
                this._globalService.showModal(result.redirectionUrl, 'Please wait');
            } else {
                // When there's no errors, just navigate to the next page...
                this._router.navigate(['form', state.formId, 'app', state.applicationId, { page: nextMenuItem.pageId }]);
            }
        } else {
            this._router.navigate(['dashboard']);
        }
    }

    private normalizeFirstValidationPage(firstPage?: FirstValidationPage | null): { pageId: number | null, subPageId: number | null } {
        return {
            pageId: firstPage?.pageId ?? null,
            subPageId: firstPage?.subpageId ?? firstPage?.subpage ?? null,
        };
    }

    private validateTargetPageServerSide(pageId: number, subPageId?: number | null): void {
        this._validationService.validateApplicationServerSide(this.applicationData ?? {}, pageId, subPageId ?? undefined)
            .pipe(take(1))
            .subscribe({
                next: result => {
                    this._validationService.queueServerValidationRedirect(result, pageId, subPageId);
                    this._validationService.applyPendingServerValidationForPage(pageId, subPageId);
                },
                error: err => console.error('Server validation failed after submit redirect', err),
            });
    }

    /**
     * check visibility rules of the current menu item, return true when visible else false.
     *
     * @returns a flag that indicates if the specified menu item is visible.
     */
    private getNavItemVisibilityStatus(visibilityRule: TruthCondition, applicationData: any, surveyResult: any): boolean {
        if (!visibilityRule) {
            return true;
        }

        let show = visibilityRule.ruleType === RuleType.Show;
        let visible = this._ruleEvaluatorService.evaluate(visibilityRule, applicationData, null, surveyResult);
        return show ? visible : !visible;
    }

    private getNextMenuItem(menuItem?: FormNavigationItem): FormNavigationItem
    {
        if (!menuItem) {
            return null;
        }

        if (menuItem.pageId && menuItem.controlId) {
            let parent = this.findMenuItem(this._menuItems, menuItem.pageId, null);
            let next = this.traverse(parent.subItems, menuItem.sortIndex);
            if (next) {
                return next;
            }

            // Find the previous most accessible item before "parent"
            next = this.traverse(this._menuItems, parent.sortIndex);
            if (next) {
                return next;
            }

            return null;
        }

        // Find the next most accessible item before the current one
        let next = this.traverse(this._menuItems, menuItem.sortIndex);
        if (next) {
            return next;
        }

        return null;
    }

    private getPreviousMenuItem(menuItem: FormNavigationItem): FormNavigationItem
    {
        if (!menuItem) {
            return null;
        }

        if (menuItem.pageId && menuItem.controlId) {
            let parent = this.findMenuItem(this._menuItems, menuItem.pageId, null);
            let prev = this.traverse(parent.subItems, menuItem.sortIndex, true);
            if (prev) {
                return prev;
            }

            // Find the previous most accessible item before "parent"
            prev = this.traverse(this._menuItems, parent.sortIndex, true);
            if (prev) {
                return prev;
            }

            return null;
        }

        // Find the previous most accessible item before the current one
        let prev = this.traverse(this._menuItems, menuItem.sortIndex, true);
        if (prev) {
            return prev;
        }

        return null;
    }

    /**
     * Checks to see if the item is a sub-menu item,
     * if it is, check that it's also the last accessible sub-menu item of it's parent.
     * If the menu item has no parents, it is the last accessible item (as it's not part of a group).
     *
     * @param menuItem The menu item to check.
     * @returns A flag indicating if this menu item is the last accessible menu item in a group.
     */
    private isLastAccessibleSubMenuItem(menuItem: FormNavigationItem): boolean
    {
        if (menuItem?.controlId) {
            const parent = this.findMenuItem(this._menuItems, menuItem.pageId, null);
            const siblings = parent?.subItems?.slice().reverse() || [];
            for (let child of siblings) {
                if (this.accessible(child)) {
                    return child.controlId == menuItem.controlId;
                }
            }
        }

        return true;
    }

    /**
     * Ensures that the current form state is valid, and takes the user
     * to the first accessible page based on entitlements and business rules.
     *
     * @returns a flag indicating if the state is valid.
     */
    private checkValidState(): boolean
    {
        if (this._formService.currentState?.pageId) {
            let menuItem = this.findMenuItem(this._menuItems, this._formService.currentState.pageId);

            if (!menuItem?.visible()) {
                menuItem = this.traverse(this._menuItems);

                if (menuItem) {
                    this.navigate(menuItem);
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Iterates the menu structure for the first accessible menu item or sub-menu item starting with the current item.
     * If the current item has children, the first accessible child.
     * If there are no accessible children, the next accessible sibling or its children.
     * If there are no accessible siblings, the first accessible item in the menu starting at the beginning.
     *
     * @param menuItem The FormNavigationItem to start with.
     * @returns The first accessible menu item found.
     */
    private findFirstAccessibleSubMenu(menuItem: FormNavigationItem): FormNavigationItem
    {
        // NO accessible sub-items. Use menuItem next sibling.
        if (!this.accessible(menuItem)) {
            let sibling = this.getNextMenuItem(menuItem);
            if (sibling) {
                return this.findFirstAccessibleSubMenu(sibling);
            }

            return this.traverse(this._menuItems);
        }

        if (menuItem?.subItems?.length) {
            for (let child of menuItem.subItems) {
                if (this.accessible(child)) {
                    if (child.subItems?.length) {
                        return this.findFirstAccessibleSubMenu(child);
                    }

                    return child;
                }
            }

            // NO accessible sub-items. Use menuItem next sibling.
            let sibling = this.getNextMenuItem(menuItem);
            if (sibling) {
                return this.findFirstAccessibleSubMenu(sibling);
            }
        }

        if (menuItem?.controlId) {
            if (this.accessible(menuItem)) {
                // This is a currently accessible sub-menu item.
                return menuItem;
            }

            let parent = this.findMenuItem(this._menuItems, menuItem.pageId, null);
            return this.findFirstAccessibleSubMenu(parent);
        }

        return this.accessible(menuItem)
            ? menuItem
            : this.traverse(this._menuItems);
    }

    private accessible(item: FormNavigationItem): boolean
    {
        if (!item || item.accessLevel == AccessLevel.NoAccess) {
            return false;
        }

        // visibility is overridden by parent if parent is hidden.
        if (item.controlId) {
            let parent = this.findMenuItem(this._menuItems, item.pageId, null);
            if (parent && !parent.visible()) {
                return false;
            }
        }

        return item.visible(); // this.getNavItemVisibilityStatus(item?.visibilityRule, this.applicationData, this.surveyResult);
    }

    private _traverseUp(items: FormNavigationItem[], fromIndex: number = -1): FormNavigationItem
    {
        for (let i = (fromIndex - 1); i >= 0; i--) {
            let item = items[i];
            if (this.accessible(item)) {
                if (item.subItems?.length) {
                    item = this.traverse(item.subItems, item.subItems.length, true);
                }

                if (item) {
                    return item;
                }
            }
        }

        return null;
    }

    private _traverseDown(items: FormNavigationItem[], fromIndex: number = -1): FormNavigationItem
    {
        for (let i = (fromIndex + 1); i < items.length; i++) {
            let item = items[i];
            if (this.accessible(item)) {
                if (item.subItems?.length) {
                    item = this.traverse(item.subItems);
                }

                if (item) {
                    return item;
                }
            }
        }

        return null;
    }

    private traverse(items: FormNavigationItem[], fromIndex: number = -1, reverse: boolean = false): FormNavigationItem
    {
        if (!items) {
            return null;
        }

        if (reverse) {
            return this._traverseUp(items, fromIndex);
        }

        return this._traverseDown(items, fromIndex)
    }

    private checkOnline(): Observable<boolean> {
        return this._globalService.verifyConnectivity()
            .pipe(catchError(err => {
                this._alertService.warning('Cannot connect to the server');
                return of(false);
            }));
    }
}
